import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '../../components/Header';
import Load from '../../components/Load';
import { Alert, Snackbar, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import IuguConfigForm from '../../components/admin/paymentmethods/IuguConfigForm';
import {
  IuguAmbiente,
  IuguConfig,
  IuguConfigInput,
  fetchAll as fetchAllIugu,
  save as saveIugu,
  setActive as setActiveIugu,
} from '../../services/iuguConfigService';

const PaymentMethods: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [testeConfig, setTesteConfig] = useState<IuguConfig | null>(null);
  const [producaoConfig, setProducaoConfig] = useState<IuguConfig | null>(null);
  const [activeEnv, setActiveEnv] = useState<IuguAmbiente | null>(null);

  const testeRef = useRef<HTMLDivElement | null>(null);
  const prodRef = useRef<HTMLDivElement | null>(null);

  const showError = (message: string) => setSnackbar({ open: true, message, severity: 'error' });
  const showSuccess = (message: string) => setSnackbar({ open: true, message, severity: 'success' });
  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchAllIugu();
      const byEnv = (env: IuguAmbiente) => items.find((i) => i.ambiente === env) || null;
      const teste = byEnv('TESTE');
      const prod = byEnv('PRODUCAO');
      setTesteConfig(teste);
      setProducaoConfig(prod);
      const active = items.find((i) => i.ativo);
      setActiveEnv(active ? active.ambiente : null);
    } catch (e: any) {
      showError(e?.message || 'Erro ao carregar configurações da IUGU.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (env: IuguAmbiente, data: IuguConfigInput, id?: string) => {
    const divRef = env === 'TESTE' ? testeRef : prodRef;
    try {
      const saved = await saveIugu(env, data, id);
      if (env === 'TESTE') setTesteConfig(saved);
      else setProducaoConfig(saved);
      if (saved.ativo) setActiveEnv(saved.ambiente);
      showSuccess('Configurações salvas com sucesso.');
      setTimeout(() => {
        divRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        divRef.current?.focus();
      }, 50);
      return saved;
    } catch (e: any) {
      showError(e?.message || 'Erro ao salvar configurações.');
      throw e;
    }
  };

  const isTesteReady = Boolean(testeConfig?.id);
  const isProdReady = Boolean(producaoConfig?.id);
  const selectedToggle = useMemo(() => activeEnv || null, [activeEnv]);

  const onChangeActive = async (_: any, newEnv: IuguAmbiente | null) => {
    if (!newEnv || newEnv === activeEnv) return;
    const target = newEnv === 'TESTE' ? testeConfig : producaoConfig;
    if (!target?.id) {
      showError('Salve as configurações deste ambiente antes de ativar.');
      return;
    }
    try {
      const ok = await setActiveIugu(target.id);
      if (ok) {
        setActiveEnv(newEnv);
        setTesteConfig((prev) => (prev ? { ...prev, ativo: newEnv === 'TESTE' } : prev));
        setProducaoConfig((prev) => (prev ? { ...prev, ativo: newEnv === 'PRODUCAO' } : prev));
        showSuccess('Ambiente ativo atualizado.');
      } else {
        showError('Falha ao ativar ambiente.');
      }
    } catch (e: any) {
      showError(e?.message || 'Erro ao ativar ambiente.');
    }
  };

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />
        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                  <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3 align-items-center">
                    <button
                      type="button"
                      className="btn btn-link text-white p-0 d-inline-block mt-2 me-4"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                    <h4 className="font-xs text-white fw-600 mb-0 mt-2 flex-grow-1">Meios de pagamento</h4>
                  </div>
                </div>

                <div className="mb-3">
                  <Typography variant="subtitle1" gutterBottom>
                    Ambiente ativo (IUGU)
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={selectedToggle}
                    onChange={onChangeActive}
                    aria-label="Ambiente ativo da IUGU"
                  >
                    <ToggleButton value="TESTE" disabled={!isTesteReady} aria-label="Homologação (TESTE)">
                      Homologação (TESTE)
                    </ToggleButton>
                    <ToggleButton value="PRODUCAO" disabled={!isProdReady} aria-label="Produção (PRODUCAO)">
                      Produção (PRODUCAO)
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {(!isTesteReady || !isProdReady) && (
                    <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: 8 }}>
                      Salve as configurações do ambiente antes de ativar.
                    </Typography>
                  )}
                </div>

                {loading && <Load />}

                {!loading && (
                  <div className="row g-3">
                    <div className="col-12 col-lg-6" ref={testeRef}>
                      <IuguConfigForm
                        title="Homologação (TESTE)"
                        env="TESTE"
                        initialData={testeConfig}
                        onSubmit={(data: IuguConfigInput, id?: string) => handleSave('TESTE', data, id)}
                        onAfterSave={(cfg) => setTesteConfig(cfg)}
                      />
                    </div>
                    <div className="col-12 col-lg-6" ref={prodRef}>
                      <IuguConfigForm
                        title="Produção (PRODUCAO)"
                        env="PRODUCAO"
                        initialData={producaoConfig}
                        onSubmit={(data: IuguConfigInput, id?: string) => handleSave('PRODUCAO', data, id)}
                        onAfterSave={(cfg) => setProducaoConfig(cfg)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

export default PaymentMethods;

